import { useMemo, useState } from 'react';
import { P2GBox, P2GButton, P2GContainer, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { Integration } from '~/lib/api/account-linking/types';
import { IntegrationRow } from './Integration-row';
import { OnboardingTitle } from '../rules/onboarding-title';
import { SidePanelContent } from '~/components/sidepanel-section';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { AppSurface } from '~/components/surface/app-surface';
import OnboardingScreenSpacing from '../spacing-consts';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

type props = {
  isXs: boolean;
  signupIntegrations: string[];
  justLinked?: string;
  integrations: Integration[];
  onDone: (integrations: string[]) => void;
  onSkip: (integrations: string[]) => void;
  priority: string[];
};

const Message = ({ primaryIntegration }: { primaryIntegration: Integration | null }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <>
      <P2GText variant="subtitle2" component="div">
        {!primaryIntegration ? (
          <>{t('SETUP_SALES_CHANNELS_SELECTION_NO_PRIMARY', "You're almost there!")}</>
        ) : (
          <>
            {t('SETUP_SALES_CHANNELS_SELECTION_PRIMARY', "We'll help you connect your {{name}} store first.", {
              name: primaryIntegration.name
            })}
          </>
        )}
      </P2GText>
      <P2GText>
        {t('SETUP_SALES_CHANNELS_SELECTION_FINAL_STEP', 'This is the final step to shipping automation.')}
      </P2GText>
    </>
  );
};

const IntegrationSelectionScreen = ({
  justLinked,
  signupIntegrations,
  isXs,
  priority,
  integrations,
  onDone,
  onSkip
}: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const [integrationSelection, setIntergrationSelection] = useState<string[]>(
    signupIntegrations
      ? signupIntegrations.filter((x) => integrations.some((i) => i.slug === x && !i.accounts.length))
      : []
  );

  const mostPopular = useMemo(() => integrations.filter((x) => priority.includes(x.slug)), [integrations, priority]);
  const other = useMemo(() => integrations.filter((x) => !priority.includes(x.slug)), [integrations, priority]);

  const primaryIntegration = useMemo(
    () => integrations?.find((x) => x.slug === integrationSelection[0]),
    [integrations, integrationSelection]
  );

  return (
    <>
      <OnboardingScreenContentWrapper>
        <P2GBox textAlign="center">
          <SidePanelContent>
            <OnboardingTitle
              text="Time to connect your <span>stores</span>"
              translationKey="SETUP_SALES_CHANNELS_SELECTION_TITLE"
            />
            <P2GText variant="subtitle1">
              {t(
                'SETUP_SALES_CHANNELS_SELECTION_SUBTITLE',
                'We’ll start with one, then you can connect the rest from your dashboard. Select your most used channel first.'
              )}
            </P2GText>
          </SidePanelContent>
        </P2GBox>
        <P2GBox sx={OnboardingScreenSpacing}>
          <P2GContainer maxWidth="md">
            <P2GBox display="flex" flexDirection="column" gap={3}>
              {mostPopular.length > 0 && (
                <AppSurface>
                  <P2GBox textAlign="left" mb={2}>
                    <P2GText variant="subtitle2">
                      {t('SETUP_SALES_CHANNELS_SELECTION_MOST_POPULAR', 'Most Popular')}
                    </P2GText>
                  </P2GBox>
                  <P2GGrid container spacing={1}>
                    {mostPopular.map((i) => (
                      <P2GGrid item key={i.slug} xs={6} sm={3}>
                        <IntegrationRow
                          done={justLinked === i.slug}
                          i={i}
                          integrationSelection={integrationSelection}
                          setIntergrationSelection={setIntergrationSelection}
                        />
                      </P2GGrid>
                    ))}
                  </P2GGrid>
                </AppSurface>
              )}
              {other.length > 0 && (
                <AppSurface>
                  <P2GBox textAlign="left" mb={2}>
                    <P2GText variant="subtitle2">
                      {t('SETUP_SALES_CHANNELS_SELECTION_OTHER_CHANNELS', 'Other Channels')}
                    </P2GText>
                  </P2GBox>
                  <P2GGrid container spacing={1}>
                    {other.map((i) => (
                      <P2GGrid item key={i.slug} xs={6} sm={3}>
                        <IntegrationRow
                          i={i}
                          integrationSelection={integrationSelection}
                          setIntergrationSelection={setIntergrationSelection}
                        />
                      </P2GGrid>
                    ))}
                  </P2GGrid>
                </AppSurface>
              )}
            </P2GBox>
            {isXs && (
              <P2GBox mt={2}>
                <AppSurface>
                  <P2GBox textAlign="center">
                    <Message primaryIntegration={primaryIntegration} />
                  </P2GBox>
                </AppSurface>
              </P2GBox>
            )}
          </P2GContainer>
        </P2GBox>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar
        skip={
          <P2GButton variant="text" onClick={() => onSkip(integrationSelection)}>
            {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
          </P2GButton>
        }
        next={
          <P2GButton
            fullWidth={isXs}
            size="large"
            disabled={!justLinked && integrationSelection.length === 0}
            onClick={() => onDone(integrationSelection)}
            variant="primary"
            style={{ minWidth: 'unset' }}
          >
            {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
          </P2GButton>
        }
      >
        {!isXs && <Message primaryIntegration={primaryIntegration} />}
      </OnboardingActionBar>
    </>
  );
};
export default IntegrationSelectionScreen;
